<template>
  <div class="bg-white w-100pc h-100pc">
    <v-container
      v-if="loaded"
      class="mxw-800 mx-auto py-12"
    >
      <div
        ref="progressBar"
        :aria-label="$bct('Progress indicator - question') + ' ' + stepCount"
        class="px-4 focus-invisible"
        role="progressbar"
        tabindex="0"
        aria-live
      >
        <v-progress-linear
          v-model="progress"
          :indeterminate="!loaded"
          class="focus-invisible mb-4"
          color="primary"
        />
      </div>

      <div class="px-4">
        <QuestionSet
          v-model="form"
          @back="backFromQuestion($event)"
          @change:attachments="loadAttachments"
          @next="forwardFromQuestion($event)"
          :attachment-owner-id="form.id"
          :attachment-owner-type="'Form'"
          :attachments="attachments"
          :processing="processing"
          :questions="validQuestions"
          :readonly="form.locked"
          :schema="formSchema.definition"
          :section="section"
          :transition-name="transitionName"
          key-name="question"
        />
      </div>
    </v-container>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useQuestionable from '@/shared/composables/useQuestionable';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useStepper from '@/shared/composables/useStepper';

const $bct = inject('translator');

const { api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { updateQuery } = useRouterHelper();

const attachments = ref([]);
const form = ref(null);
const formSchema = ref(null);
const formSchemaId = ref(null);
const processing = ref(false);
const progressBar = ref(null);

const { questions, loadQuestions, questionsLoaded, validateAnswers, validQuestions } =
  useQuestionable({
    ownerDataTypeRef: ref('Schema'),
    ownerIdRef: formSchemaId,
    syncedObjectRef: form,
  });

const { progress, section, stepBack, stepCount, stepForward, stepTotal, transitionName } =
  useStepper({
    processing,
    route,
    updateQuery,
  });

const loaded = computed(() => {
  return form.value && formSchema.value && questionsLoaded.value && questions.value.length > 0;
});

watch(
  () => loaded.value,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      if (route.query.step) stepCount.value = parseInt(route.query.step, 10);
      else stepCount.value = 0;

      if (!stepTotal.value) await validateAnswers();

      recalculateStepTotal();

      if (route.query.section) {
        section.value = route.query.section;
        progressBar.value?.focus();
        progress.value = (stepCount.value / stepTotal.value) * 100;
      } else {
        start();
      }
    }
  },
  { immediate: true },
);

watch(
  () => questionsLoaded.value,
  (newVal) => {
    if (newVal) recalculateStepTotal();
  },
);

watch(
  () => route.query.section,
  async (newVal) => {
    section.value = null;
    stepCount.value = parseInt(route.query.step, 10);

    if (!stepTotal.value) await validateAnswers();

    recalculateStepTotal();
    if (newVal) {
      section.value = newVal;
      progressBar.value?.focus();
    } else {
      section.value = 'question-0';
    }
    progress.value = (stepCount.value / stepTotal.value) * 100;
  },
);

onMounted(load);

async function load() {
  await loadForm();
  formSchema.value = store.state.schemas[formSchemaId.value];
  void loadAttachments();
  await loadQuestions();
}

async function loadForm() {
  const response = await api.manager.form.get(route.params.formId);
  const record = response.data.data;
  formSchemaId.value = record.relationships.schema.data.id;
  form.value = { id: record.id, ...record.attributes };
}

async function loadAttachments() {
  const params = {
    owner_type: 'Form',
    owner_id: form.value.id,
  };
  const response = await api.member.attachment.index(params);
  if (response?.status !== 200) return;
  attachments.value = response.data;
}

async function saveForm() {
  const response = await api.manager.form.update(form.value.id, { custom: form.value.custom });
  const record = response.data.data;
  form.value.custom = record.attributes.custom || {};
}

async function backFromQuestion(index) {
  processing.value = true;
  if (!form.value.locked) {
    await saveForm();
    await validateAnswers();
  }
  recalculateStepTotal();

  if (index - 1 < 0) {
    await router.push({ name: 'ProviderLicenseIndex' });
  } else {
    stepBack(`question-${index - 1}`);
  }
}

async function finish() {
  processing.value = true;
  if (!form.value.locked) {
    await api.manager.form.update(form.value.id, {
      custom: form.value.custom,
      submitted: true,
    });
    eventBus.chime('Application submitted');
  }

  await router.push({ name: 'ProviderLicenseIndex' });
}

async function forwardFromQuestion(index) {
  processing.value = true;
  if (!form.value.locked) {
    await saveForm();
    await validateAnswers();
  }
  recalculateStepTotal();

  if (index + 1 >= validQuestions.value.length) {
    await finish();
  } else {
    stepForward(`question-${index + 1}`);
  }
}

function recalculateStepTotal() {
  stepTotal.value = validQuestions.value.length;
  progress.value = (stepCount.value / stepTotal.value) * 100;
}

function start() {
  processing.value = true;
  if (validQuestions.value.length > 0) {
    stepForward('question-0');
  } else {
    stepForward('finish');
  }
}
</script>
